import moment from "moment";
import getSymbolFromCurrency from "currency-symbol-map";
import { TFunction } from "i18next";

import {
  BonusActivationRule,
  BonusFreeSpinItem,
  BonusReward,
} from "types/playerBonuses";

export const DISPLAYED_WAGER_GAMES = 8;

export const EMPTY_VALUE = "—";

export const formatBonusDate = (date?: string | null): string => {
  if (!date) {
    return EMPTY_VALUE;
  }

  return moment(date).format("DD.MM.YYYY HH:mm");
};

export const getBonusStatusLabel = (
  status: string | undefined | null,
  historyType: string | undefined,
  t: TFunction,
): string => {
  if (!status) {
    return EMPTY_VALUE;
  }

  const statusKey = status.toLowerCase();

  const labels: Record<string, string> = {
    claimed: t("claimed"),
    wagering: t("wagering"),
    completed: t("completed"),
    canceled: t("cancelled"),
    cancelled: t("cancelled"),
    expired: t("expired"),
  };

  if (labels[statusKey]) {
    return labels[statusKey];
  }

  if (historyType) {
    const historyKey = historyType.toLowerCase();

    if (labels[historyKey]) {
      return labels[historyKey];
    }
  }

  return status;
};

export const clampWagerProgress = (
  progress?: number | null,
): number | null => {
  if (progress === undefined || progress === null) {
    return null;
  }

  return Math.min(100, Math.max(0, progress));
};

export const formatWagerMultiplier = (value?: number | null): string => {
  if (value === undefined || value === null) {
    return EMPTY_VALUE;
  }

  return `${value}x`;
};

export const formatPayoutValue = (value?: number | null): string => {
  if (value === undefined || value === null) {
    return EMPTY_VALUE;
  }

  return String(value);
};

export const formatCurrencyAmount = (
  currency: string,
  value?: number | string | null,
): string => {
  if (value === undefined || value === null || value === "") {
    return EMPTY_VALUE;
  }

  const symbol = getSymbolFromCurrency(currency) ?? "";

  return `${symbol}${value}`;
};

export const formatRewardShort = (
  reward: BonusReward,
  t: TFunction,
): string => {
  const { type, currency, config } = reward;

  switch (type) {
    case "depositPercentage":
      return `${config.value}% up to ${formatCurrencyAmount(currency, config.max)}`;
    case "depositStatic":
      return formatCurrencyAmount(currency, config.value);
    case "freespins":
      return `${config.totalAmount ?? ""} FS`.trim();
    case "loyalty":
      return `${config.points} ${t("loyaltyPoints")}`;
    case "coins":
      return `${config.points} ${t("coins")}`;
    case "ranking":
      return `${config.points ?? config.value} ${t("rankingReward")}`;
    default:
      return type;
  }
};

export const formatRewardsShortList = (
  rewards: BonusReward[],
  t: TFunction,
  limit = 2,
): string[] => {
  return rewards.slice(0, limit).map((reward) => formatRewardShort(reward, t));
};

export const formatRemainingTime = (ms?: number | null): string | null => {
  if (ms === undefined || ms === null || ms <= 0) {
    return null;
  }

  const duration = moment.duration(ms);
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

export const getBonusImage = (item: {
  images?: { desktop?: string | null };
  image?: string;
}): string | undefined => item.images?.desktop || item.image;

export const formatWagerBalanceLabel = (
  balance: string,
  t: TFunction,
): string => {
  if (balance === "real") {
    return t("realBalanceEligible");
  }

  if (balance === "bonus") {
    return t("bonusBalanceEligible");
  }

  return balance;
};

export const getWagerBalanceBadgeClass = (balance: string): string => {
  if (balance === "real") {
    return "badge--success";
  }

  if (balance === "bonus") {
    return "badge--info";
  }

  return "badge--neutral";
};

export const formatActivationRuleTitle = (
  type: string,
  t: TFunction,
): string => {
  if (t(type) !== type) {
    return t(type);
  }

  return type;
};

export const getActivationRuleDetails = (
  rule: BonusActivationRule,
  t: TFunction,
): string[] => {
  const details: string[] = [];
  const { currency, config } = rule;

  if (config.min !== undefined && config.min !== null && config.min !== "") {
    details.push(`${t("min")}: ${formatCurrencyAmount(currency, config.min)}`);
  }

  if (config.max !== undefined && config.max !== null && config.max !== "") {
    details.push(`${t("max")}: ${formatCurrencyAmount(currency, config.max)}`);
  }

  if (
    config.numberOfDeposit !== undefined &&
    config.numberOfDeposit !== null
  ) {
    details.push(`${t("numberOfDeposits")}: ${config.numberOfDeposit}`);
  }

  Object.entries(config).forEach(([key, value]) => {
    if (
      key === "min" ||
      key === "max" ||
      key === "numberOfDeposit" ||
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return;
    }

    const label = t(key) !== key ? t(key) : key;
    details.push(`${label}: ${value}`);
  });

  return details;
};

export const formatRewardFull = (
  reward: BonusReward,
  t: TFunction,
): string => {
  const short = formatRewardShort(reward, t);

  if (reward.type === "depositPercentage") {
    return `${reward.config.value}% ${t("depositBonusUpTo")} ${formatCurrencyAmount(reward.currency, reward.config.max)}`;
  }

  return short;
};

export const formatFreeSpinDetails = (
  item: BonusFreeSpinItem,
  t: TFunction,
  currencyFallback = "EUR",
): string[] => {
  const details: string[] = [];
  const currency = item.currency || currencyFallback;

  if (item.quantity !== undefined && item.quantity !== null) {
    details.push(`${t("quantity")}: ${item.quantity}`);
  }

  if (item.quantityUsed !== undefined && item.quantityUsed !== null) {
    details.push(`${t("used")}: ${item.quantityUsed}`);
  }

  if (item.spinAmount !== undefined && item.spinAmount !== null) {
    details.push(
      `${t("spinAmount")}: ${formatCurrencyAmount(currency, item.spinAmount)}`,
    );
  }

  if (item.status) {
    details.push(`${t("status")}: ${item.status}`);
  }

  if (typeof item.providerErrorMessage === "string" && item.providerErrorMessage.trim()) {
    details.push(`Provider error: ${item.providerErrorMessage.trim()}`);
  }

  if (item.expired) {
    details.push(`${t("expiresAt")}: ${formatBonusDate(item.expired)}`);
  }

  return details;
};
